import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { useTitle } from '../hooks/useTitle';
import { customToast } from '../utils/customToast';
import timezoneService, { Timezone, CurrencySymbol } from '../services/timezoneService';
import { dashboardService, SubscriptionInfo } from '../services/dashboardService';
import { format } from 'date-fns';
import {
  Building,
  Package,
  Save,
  RefreshCw,
  Clock,
  DollarSign,
  AlertTriangle,
  Check,
  Globe,
  Upload,
  Image,
  CreditCard,
  Calendar,
  Users
} from 'lucide-react';
import StaffManagementTab from '../components/Settings/StaffManagementTab';
import AdminUserManagementTab from '../components/Settings/AdminUserManagementTab';

const Settings: React.FC = () => {
  const { theme } = useTheme();
  const { user, refreshUser } = useAuth();
  const { settings, loading, updateSettings, uploadFavicon, uploadLogo, getSetting, refreshSettings } = useSettings();
  const [activeCategory, setActiveCategory] = useState('general');
  const [formData, setFormData] = useState<{ [key: string]: string }>({});
  const [isSaving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [timezones, setTimezones] = useState<Timezone[]>([]);
  const [currencySymbols, setCurrencySymbols] = useState<CurrencySymbol[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  useTitle('Settings');

  useEffect(() => {
    // Initialize form data from settings with defaults
    const initialData: { [key: string]: string } = {};

    // Debug: Log what settings are loaded
    console.log('Settings loaded in Settings component:', settings);
    console.log('Settings keys:', Object.keys(settings));

    // Set default values - use shop name if available for shop users
    const shopName = user?.shop?.shop_name || 'NextGen - Shop';
    const defaults = {
      site_name: shopName,
      site_description: 'Management System & POS',
      currency_symbol: '₨',
      timezone: 'Asia/Karachi',
      low_stock_threshold: '10',
      expiry_alert_days: '30',
      print_store_name: shopName,
      print_phone: '+92 348 119 6365',
      print_address: '123 Main Street, Suite 456, Springfield, USA 12345'
    };

    // Use settings values or defaults
    Object.entries(defaults).forEach(([key, defaultValue]) => {
      initialData[key] = settings[key]?.value || defaultValue;
      console.log(`Setting ${key}:`, settings[key]?.value || 'using default', defaultValue);
    });

    console.log('Final initialData:', initialData);
    setFormData(initialData);
  }, [settings]);

  // Load timezone and currency options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const [timezonesData, currencyData] = await Promise.all([
          timezoneService.getPopularTimezones(),
          timezoneService.getCurrencySymbols()
        ]);

        setTimezones(timezonesData);
        setCurrencySymbols(currencyData);
      } catch (error) {
        console.error('Failed to load timezone/currency options:', error);
        customToast.error('Failed to load dropdown options');
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  // Load subscription info (only for shop users, not super admin)
  useEffect(() => {
    // Don't load subscription info for super admin
    if (user?.role === 'super_admin') {
      console.log('👑 Super admin detected, skipping subscription load');
      setLoadingSubscription(false);
      return;
    }

    const loadSubscription = async () => {
      try {
        console.log('🔄 Loading subscription info for user:', user?.email);
        setLoadingSubscription(true);
        const data = await dashboardService.getDashboardData('today');
        console.log('📊 Dashboard data received:', data);
        console.log('📋 Subscription object:', data.subscription);
        console.log('📋 Full data keys:', Object.keys(data));
        console.log('📋 Stats:', data.stats);

        if (data.subscription) {
          console.log('✅ Setting subscription info:', data.subscription);
          setSubscriptionInfo(data.subscription);
        } else {
          console.warn('⚠️ No subscription object in dashboard data');
          console.warn('⚠️ Data structure:', JSON.stringify(data, null, 2));
        }
      } catch (error) {
        console.error('❌ Failed to load subscription info:', error);
      } finally {
        setLoadingSubscription(false);
      }
    };

    loadSubscription();
  }, [user]);

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);

      console.log('Starting save operation...');
      console.log('Current formData:', formData);

      // Prepare settings for update
      const settingsToUpdate: { [key: string]: any } = {};
      const settingsConfig = {
        site_name: { type: 'string', category: 'general' },
        site_description: { type: 'string', category: 'general' },
        currency_symbol: { type: 'string', category: 'general' },
        timezone: { type: 'string', category: 'general' },
        print_store_name: { type: 'string', category: 'print' },
        print_phone: { type: 'string', category: 'print' },
        print_address: { type: 'string', category: 'print' },
        low_stock_threshold: { type: 'integer', category: 'inventory' },
        expiry_alert_days: { type: 'integer', category: 'inventory' }
      };

      Object.entries(formData).forEach(([key, value]) => {
        if (settingsConfig[key as keyof typeof settingsConfig] &&
          (!settings[key] || settings[key].value !== value)) {
          const config = settingsConfig[key as keyof typeof settingsConfig];
          settingsToUpdate[key] = {
            value,
            type: config.type,
            category: config.category
          };
        }
      });

      console.log('Settings to update:', settingsToUpdate);

      if (Object.keys(settingsToUpdate).length > 0) {
        await updateSettings(settingsToUpdate);
        console.log('Settings updated successfully');

        // If site_name changed, refresh user data to update shop name in profile
        if (settingsToUpdate.site_name) {
          console.log('Site name changed, refreshing user data...');
          await refreshUser();
        }
      } else {
        customToast.info('No changes to save');
        console.log('No changes to save');
      }

      setHasUnsavedChanges(false);
      setSaveSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      customToast.error('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleFaviconSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        customToast.error('Please select a valid image file');
        return;
      }

      // Validate file size (max 1MB)
      if (file.size > 1024 * 1024) {
        customToast.error('File size must be less than 1MB');
        return;
      }

      try {
        setUploadingFavicon(true);
        setSaving(true);
        await uploadFavicon(file);
        setFaviconFile(file);
        setSaveSuccess(true);

        // Refresh settings to update the UI immediately
        await refreshSettings();

        // Clear the file input to allow re-uploading the same file
        e.target.value = '';

        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (error) {
        console.error('Error uploading favicon:', error);
        customToast.error('Failed to upload favicon. Please try again.');
      } finally {
        setUploadingFavicon(false);
        setSaving(false);
      }
    }
  };

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        customToast.error('Please select a valid image file');
        return;
      }

      // Validate file size (max 2MB for logo)
      if (file.size > 2 * 1024 * 1024) {
        customToast.error('File size must be less than 2MB');
        return;
      }

      try {
        setUploadingLogo(true);
        setSaving(true);
        await uploadLogo(file);
        setLogoFile(file);
        setSaveSuccess(true);

        // Refresh settings to update the UI immediately
        await refreshSettings();

        // Clear the file input to allow re-uploading the same file
        e.target.value = '';

        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (error) {
        console.error('Error uploading logo:', error);
        customToast.error('Failed to upload logo. Please try again.');
      } finally {
        setUploadingLogo(false);
        setSaving(false);
      }
    }
  };

  // Only show subscription tab for shop users, not super admin
  const isSuperAdmin = user?.role === 'super_admin';

  const categories = [
    {
      id: 'general',
      label: 'General',
      icon: Building,
      description: 'Basic site configuration and branding',
      color: 'blue'
    },
    ...(isSuperAdmin ? [{
      id: 'user-management',
      label: 'User Management',
      icon: Users,
      description: 'Manage admin users and permissions',
      color: 'purple'
    }] : []),
    ...(!isSuperAdmin ? [{
      id: 'subscription',
      label: 'Subscription',
      icon: CreditCard,
      description: 'View your subscription and billing details',
      color: 'purple'
    }] : []),
    ...(!isSuperAdmin ? [{
      id: 'print',
      label: 'Print Settings',
      icon: Upload,
      description: 'Receipt and printer configuration',
      color: 'indigo'
    }] : []),
    ...(!isSuperAdmin ? [{
      id: 'inventory',
      label: 'Inventory',
      icon: Package,
      description: 'Stock alerts and product settings',
      color: 'green'
    }] : []),
    ...(!isSuperAdmin ? [{
      id: 'staff',
      label: 'Staff Management',
      icon: Users,
      description: 'Manage staff members and permissions',
      color: 'orange'
    }] : [])
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            Settings
          </h1>
          <p className={`mt-1 sm:mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
            Configure your store settings and preferences
          </p>
        </div>

        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3 sm:items-center">
          {hasUnsavedChanges && (
            <div className={`flex items-center space-x-2 text-xs sm:text-sm ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
              }`}>
              <AlertTriangle size={14} />
              <span>Unsaved changes</span>
            </div>
          )}

          {(uploadingFavicon || uploadingLogo) && (
            <div className={`flex items-center space-x-2 text-xs sm:text-sm ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
              }`}>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>
                {uploadingFavicon && uploadingLogo ? 'Uploading files...'
                  : uploadingFavicon ? 'Uploading favicon...'
                    : 'Uploading logo...'}
              </span>
            </div>
          )}

          {saveSuccess && (
            <div className={`flex items-center space-x-2 text-xs sm:text-sm ${theme === 'dark' ? 'text-green-400' : 'text-green-600'
              }`}>
              <Check size={14} />
              <span>Settings saved!</span>
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={isSaving || !hasUnsavedChanges || uploadingFavicon || uploadingLogo}
            className={`
              w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2.5 sm:py-2 rounded-lg font-medium transition-all touch-manipulation
              ${isSaving || !hasUnsavedChanges || uploadingFavicon || uploadingLogo
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
              }
            `}
          >
            {isSaving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span className="text-sm">{isSaving ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'} -mx-3 sm:mx-0`}>
        <nav className="flex space-x-4 sm:space-x-8 px-3 sm:px-0 overflow-x-auto">
          {categories.map((category) => {
            const Icon = category.icon;
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`
                  group flex items-center space-x-2 py-3 sm:py-4 px-1 border-b-2 font-medium text-sm transition-all whitespace-nowrap touch-manipulation min-w-max
                  ${isActive
                    ? `border-blue-500 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`
                    : `border-transparent ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300 hover:border-slate-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                  }
                `}
              >
                <Icon className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${isActive
                  ? theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  : theme === 'dark' ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-400 group-hover:text-gray-500'
                  }`} />
                <span className="text-xs sm:text-sm">{category.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className={`rounded-xl sm:rounded-2xl border p-4 sm:p-6 ${theme === 'dark'
        ? 'bg-slate-800 border-slate-700'
        : 'bg-white border-gray-200'
        }`}>
        {activeCategory === 'general' && (
          <div className="space-y-6 sm:space-y-8">
            <div>
              <h2 className={`text-lg sm:text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                General Settings
              </h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                Configure basic information about your store
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <Building className="w-4 h-4 inline mr-2" />
                  Site Name
                </label>
                <input
                  type="text"
                  value={formData.site_name || ''}
                  onChange={(e) => handleInputChange('site_name', e.target.value)}
                  className={`
                    w-full px-4 py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation
                    ${theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    } focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                  `}
                  placeholder="Enter your store name"
                />
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  This appears in the sidebar and browser title
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <Globe className="w-4 h-4 inline mr-2" />
                  Site Description
                </label>
                <input
                  type="text"
                  value="Management System & POS"
                  readOnly
                  className={`
                    w-full px-4 py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation cursor-not-allowed
                    ${theme === 'dark'
                      ? 'bg-slate-600 border-slate-500 text-gray-300'
                      : 'bg-gray-100 border-gray-300 text-gray-600'
                    } focus:ring-0 focus:outline-none
                  `}
                  placeholder="Brief description"
                />
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  This description is fixed and cannot be changed
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <Package className="w-4 h-4 inline mr-2" />
                  Application Version
                </label>
                <div className={`
                  w-full px-4 py-3 rounded-lg sm:rounded-xl border bg-gray-50 
                  ${theme === 'dark'
                    ? 'bg-slate-800 border-slate-600 text-gray-300'
                    : 'bg-gray-50 border-gray-300 text-gray-600'
                  }
                `}>
                  1.0.0
                </div>
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  Software version (read-only)
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <DollarSign className="w-4 h-4 inline mr-2" />
                  Currency Symbol
                </label>
                {loadingOptions ? (
                  <div className={`w-full px-4 py-3 rounded-lg sm:rounded-xl border ${theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-gray-400'
                    : 'bg-gray-100 border-gray-300 text-gray-500'
                    }`}>
                    Loading currency options...
                  </div>
                ) : (
                  <select
                    value={formData.currency_symbol || ''}
                    onChange={(e) => handleInputChange('currency_symbol', e.target.value)}
                    className={`
                      w-full px-4 py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation
                      ${theme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-white focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      } focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                    `}
                  >
                    <option value="">Select Currency Symbol</option>
                    {currencySymbols.map((currency) => (
                      <option key={currency.code} value={currency.symbol}>
                        {currency.symbol} - {currency.name} ({currency.code})
                      </option>
                    ))}
                  </select>
                )}
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  This symbol will be used throughout the application for all prices
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <Clock className="w-4 h-4 inline mr-2" />
                  Timezone
                </label>
                {loadingOptions ? (
                  <div className={`w-full px-4 py-3 rounded-lg sm:rounded-xl border ${theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-gray-400'
                    : 'bg-gray-100 border-gray-300 text-gray-500'
                    }`}>
                    Loading timezone options...
                  </div>
                ) : (
                  <select
                    value={formData.timezone || ''}
                    onChange={(e) => handleInputChange('timezone', e.target.value)}
                    className={`
                      w-full px-4 py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation
                      ${theme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-white focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      } focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                    `}
                  >
                    <option value="">Select Timezone</option>
                    <optgroup label="Popular Timezones">
                      {timezones.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                )}
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  This timezone will be used for all date and time displays
                </p>
              </div>
            </div>

            {/* Favicon Upload Section */}
            <div className={`border-t pt-6 sm:pt-8 ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
              <h3 className={`text-base sm:text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                Site Favicon
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    <Image className="w-4 h-4 inline mr-2" />
                    Upload Favicon
                  </label>
                  <div className={`
                    w-full p-4 sm:p-6 border-2 border-dashed rounded-lg sm:rounded-xl transition-colors touch-manipulation
                    ${uploadingFavicon ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}
                    ${theme === 'dark'
                      ? 'border-slate-600 bg-slate-700/50 hover:bg-slate-700 hover:border-slate-500'
                      : 'border-gray-300 bg-gray-50/50 hover:bg-gray-50 hover:border-gray-400'
                    }
                  `}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFaviconSelect}
                      className="hidden"
                      id="favicon-upload"
                      disabled={uploadingFavicon}
                    />
                    <label htmlFor="favicon-upload" className="cursor-pointer">
                      <div className="text-center">
                        {uploadingFavicon ? (
                          <RefreshCw className={`mx-auto h-6 w-6 sm:h-8 sm:w-8 mb-2 animate-spin ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                            }`} />
                        ) : (
                          <Upload className={`mx-auto h-6 w-6 sm:h-8 sm:w-8 mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`} />
                        )}
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          {uploadingFavicon
                            ? 'Uploading favicon...'
                            : faviconFile
                              ? faviconFile.name
                              : 'Click to upload favicon'
                          }
                        </p>
                        <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                          ICO, PNG, JPG up to 1MB
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Current Favicon
                  </label>
                  <div className={`flex items-center space-x-3 p-3 sm:p-4 rounded-lg border ${theme === 'dark' ? 'border-slate-600 bg-slate-700' : 'border-gray-200 bg-gray-50'
                    }`}>
                    <img
                      src={`${import.meta.env.VITE_API_BASE_URL?.replace('/api', '/storage') || 'http://localhost:8000/storage'}/${getSetting('site_favicon')}?v=${Date.now()}`}
                      alt={settings.site_favicon?.is_default !== false ? "Default favicon" : "Current favicon"}
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded"
                      onError={(e) => {
                        console.error('Favicon image failed to load:', e);
                        e.currentTarget.src = `${import.meta.env.VITE_API_BASE_URL?.replace('/api', '/storage') || 'http://localhost:8000/storage'}/assets/defaults/Inventro.png`;
                      }}
                    />
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      {settings.site_favicon?.is_default !== false ? 'Default favicon (NextGen - Shop)' : 'Current favicon'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Site Logo Upload Section */}
            <div className={`border-t pt-6 sm:pt-8 ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
              <h3 className={`text-base sm:text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                Site Logo
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    <Image className="w-4 h-4 inline mr-2" />
                    Upload Logo
                  </label>
                  <div className={`
                    w-full p-4 sm:p-6 border-2 border-dashed rounded-lg sm:rounded-xl transition-colors touch-manipulation
                    ${uploadingLogo ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}
                    ${theme === 'dark'
                      ? 'border-slate-600 bg-slate-700/50 hover:bg-slate-700 hover:border-slate-500'
                      : 'border-gray-300 bg-gray-50/50 hover:bg-gray-50 hover:border-gray-400'
                    }
                  `}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoSelect}
                      className="hidden"
                      id="logo-upload"
                      disabled={uploadingLogo}
                    />
                    <label htmlFor="logo-upload" className="cursor-pointer">
                      <div className="text-center">
                        {uploadingLogo ? (
                          <RefreshCw className={`mx-auto h-6 w-6 sm:h-8 sm:w-8 mb-2 animate-spin ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                            }`} />
                        ) : (
                          <Upload className={`mx-auto h-6 w-6 sm:h-8 sm:w-8 mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`} />
                        )}
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          {uploadingLogo
                            ? 'Uploading logo...'
                            : logoFile
                              ? logoFile.name
                              : 'Click to upload logo'
                          }
                        </p>
                        <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                          PNG, JPG, SVG up to 2MB
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    Current Logo
                  </label>
                  <div className={`flex items-center space-x-3 p-3 sm:p-4 rounded-lg border ${theme === 'dark' ? 'border-slate-600 bg-slate-700' : 'border-gray-200 bg-gray-50'
                    }`}>
                    <img
                      src={`${import.meta.env.VITE_API_BASE_URL?.replace('/api', '/storage') || 'http://localhost:8000/storage'}/${getSetting('site_logo')}?v=${Date.now()}`}
                      alt={settings.site_logo?.is_default !== false ? "Default logo" : "Current logo"}
                      className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded"
                      onError={(e) => {
                        console.error('Logo image failed to load:', e);
                        e.currentTarget.src = `${import.meta.env.VITE_API_BASE_URL?.replace('/api', '/storage') || 'http://localhost:8000/storage'}/assets/defaults/Inventro.png`;
                      }}
                    />
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      {settings.site_logo?.is_default !== false ? 'Default logo (NextGen - Shop)' : 'Current logo'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeCategory === 'inventory' && (
          <div className="space-y-6 sm:space-y-8">
            <div>
              <h2 className={`text-lg sm:text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                Inventory Settings
              </h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                Configure stock management and alerts
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <Package className="w-4 h-4 inline mr-2" />
                  Low Stock Threshold
                </label>
                <input
                  type="number"
                  value={formData.low_stock_threshold || ''}
                  onChange={(e) => handleInputChange('low_stock_threshold', e.target.value)}
                  className={`
                    w-full px-4 py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation
                    ${theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    } focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                  `}
                  placeholder="10"
                  min="1"
                />
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  Alert when stock falls below this quantity
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Expiry Alert Days
                </label>
                <input
                  type="number"
                  value={formData.expiry_alert_days || ''}
                  onChange={(e) => handleInputChange('expiry_alert_days', e.target.value)}
                  className={`
                    w-full px-4 py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation
                    ${theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    } focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                  `}
                  placeholder="30"
                  min="1"
                />
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  Show expiry alerts this many days in advance
                </p>
              </div>
            </div>
          </div>
        )}

        {activeCategory === 'subscription' && (
          <div className="space-y-6 sm:space-y-8">
            <div>
              <h2 className={`text-lg sm:text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                Subscription & Billing
              </h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                View your subscription details and billing information
              </p>
            </div>

            {loadingSubscription ? (() => {
              const skeletonColor = theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300';
              return (
                <div className="space-y-4 sm:space-y-6">
                  {/* Status Card Skeleton */}
                  <div className={`p-6 rounded-xl border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        {/* Icon placeholder */}
                        <div className={`p-3 rounded-lg w-12 h-12 animate-pulse ${skeletonColor}`} />
                        <div className="flex-1">
                          {/* Title skeleton */}
                          <div className={`h-5 w-40 rounded animate-pulse mb-2 ${skeletonColor}`} />
                          {/* Subtitle skeleton */}
                          <div className={`h-4 w-24 rounded animate-pulse ${skeletonColor}`} />
                        </div>
                      </div>
                      {/* Trial badge placeholder */}
                      <div className={`h-6 w-16 rounded-full animate-pulse ${skeletonColor}`} />
                    </div>
                  </div>

                  {/* Details Grid Skeleton - 4 cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {[1, 2, 3, 4].map((idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                          }`}
                      >
                        {/* Icon + Label */}
                        <div className="flex items-center space-x-2 mb-2">
                          <div className={`w-4 h-4 rounded animate-pulse ${skeletonColor}`} />
                          <div className={`h-3 w-24 rounded animate-pulse ${skeletonColor}`} />
                        </div>
                        {/* Value skeleton */}
                        <div className={`h-6 w-32 rounded animate-pulse ${skeletonColor}`} />
                      </div>
                    ))}
                  </div>

                  {/* Support Message Skeleton */}
                  <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div className="space-y-2">
                      <div className={`h-4 w-56 rounded animate-pulse ${skeletonColor}`} />
                      <div className={`h-3 w-full rounded animate-pulse ${skeletonColor}`} />
                      <div className={`h-3 w-4/5 rounded animate-pulse ${skeletonColor}`} />
                    </div>
                  </div>
                </div>
              );
            })() : subscriptionInfo ? (
              <div className="space-y-4 sm:space-y-6">
                {/* Subscription Status Card */}
                <div className={`p-6 rounded-xl border ${subscriptionInfo.subscription_status === 'active'
                  ? theme === 'dark' ? 'bg-indigo-900/30 border-indigo-700/50' : 'bg-indigo-50 border-indigo-200'
                  : subscriptionInfo.subscription_status === 'trial'
                    ? theme === 'dark' ? 'bg-purple-900/30 border-purple-700/50' : 'bg-purple-50 border-purple-200'
                    : theme === 'dark' ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
                  }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-3 rounded-lg ${subscriptionInfo.subscription_status === 'active'
                        ? 'bg-indigo-600'
                        : subscriptionInfo.subscription_status === 'trial'
                          ? 'bg-purple-600'
                          : 'bg-red-600'
                        }`}>
                        <CreditCard className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                          {subscriptionInfo.subscription_status === 'active'
                            ? 'Active Subscription'
                            : subscriptionInfo.subscription_status === 'trial'
                              ? 'Trial Period'
                              : subscriptionInfo.subscription_status === 'suspended'
                                ? 'Subscription Suspended'
                                : 'Subscription Expired'}
                        </h3>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                          {subscriptionInfo.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'} Plan
                        </p>
                      </div>
                    </div>
                    {subscriptionInfo.is_trial && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-600 text-white">
                        TRIAL
                      </span>
                    )}
                  </div>
                </div>

                {/* Subscription Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {subscriptionInfo.subscription_starts_at && (
                    <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Calendar className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                          Subscription Started
                        </label>
                      </div>
                      <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                        {format(new Date(subscriptionInfo.subscription_starts_at), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  )}

                  {subscriptionInfo.next_renewal_date && (
                    <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Calendar className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                          Next Renewal Date
                        </label>
                      </div>
                      <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                        {format(new Date(subscriptionInfo.next_renewal_date), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  )}

                  {subscriptionInfo.days_remaining !== null && subscriptionInfo.days_remaining !== undefined && (
                    <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Clock className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                          Days Remaining
                        </label>
                      </div>
                      <p className={`text-2xl font-bold ${subscriptionInfo.days_remaining <= 7
                        ? 'text-red-600'
                        : subscriptionInfo.days_remaining <= 30
                          ? 'text-yellow-600'
                          : 'text-green-600'
                        }`}>
                        {Math.round(subscriptionInfo.days_remaining)} days
                      </p>
                      {subscriptionInfo.days_remaining <= 7 && (
                        <p className="text-xs text-red-600 mt-1 font-medium">
                          ⚠️ Renewal required soon!
                        </p>
                      )}
                    </div>
                  )}

                  {/* Only show billing cycle if not on trial */}
                  {!subscriptionInfo.is_trial && (
                    <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Package className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                          Billing Cycle
                        </label>
                      </div>
                      <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                        {subscriptionInfo.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Contact Support Message */}
                <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-indigo-900/30 border-indigo-700/50' : 'bg-indigo-50 border-indigo-200'
                  }`}>
                  <p className={`text-sm ${theme === 'dark' ? 'text-indigo-300' : 'text-indigo-700'}`}>
                    <strong>Need to update your subscription?</strong> Contact support to upgrade, downgrade, or modify your billing details.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  No subscription information available
                </p>
              </div>
            )}
          </div>
        )}

        {activeCategory === 'print' && (
          <div className="space-y-6 sm:space-y-8">
            <div>
              <h2 className={`text-lg sm:text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                Print Settings
              </h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                Configure receipt header information
              </p>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <Building className="w-4 h-4 inline mr-2" />
                  Store Name
                </label>
                <input
                  type="text"
                  value={formData.print_store_name || ''}
                  onChange={(e) => handleInputChange('print_store_name', e.target.value)}
                  className={`
                    w-full px-4 py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation
                    ${theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    } focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                  `}
                  placeholder="NextGen - Shop"
                />
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  This will appear as the main title on receipts
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <Globe className="w-4 h-4 inline mr-2" />
                  Phone Number
                </label>
                <input
                  type="text"
                  value={formData.print_phone || ''}
                  onChange={(e) => handleInputChange('print_phone', e.target.value)}
                  className={`
                    w-full px-4 py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation
                    ${theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    } focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                  `}
                  placeholder="+92 348 119 6365"
                />
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  Contact number displayed on receipts
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  <Globe className="w-4 h-4 inline mr-2" />
                  Address
                </label>
                <textarea
                  value={formData.print_address || ''}
                  onChange={(e) => handleInputChange('print_address', e.target.value)}
                  rows={3}
                  className={`
                    w-full px-4 py-3 rounded-lg sm:rounded-xl border transition-colors resize-none touch-manipulation
                    ${theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    } focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                  `}
                  placeholder="Zayam Koruna, Khan Baba Garhi&#10;Bakhshu Pul, Peshawar"
                />
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                  Complete address displayed on receipts (use line breaks for multiple lines)
                </p>
              </div>
            </div>
          </div>
        )}

        {activeCategory === 'staff' && (
          <StaffManagementTab />
        )}

        {activeCategory === 'user-management' && (
          <AdminUserManagementTab />
        )}
      </div>
    </div>
  );
};

export default Settings;